"use client";

import { useEffect, useState } from "react";

import { CVS_REVIEWED_COUNT } from "@/lib/proof-stats";

// One request per page load, shared by every component that shows the count
// (the paywall card mounts in several places on the review page).
let pending: Promise<number | null> | null = null;
let loaded: number | null = null;

function loadCvsReviewedCount(): Promise<number | null> {
  pending ??= fetch("/api/proof-stats")
    .then((response) => (response.ok ? response.json() : null))
    .then((body: unknown) => {
      const value =
        body && typeof body === "object" && "cvsReviewed" in body
          ? body.cvsReviewed
          : null;
      if (typeof value === "number" && value > 0) {
        loaded = value;
        return value;
      }
      return null;
    })
    .catch(() => null);
  return pending;
}

/**
 * The live completed-free-review count, starting from the static fallback so
 * the proof line renders immediately and never shows an empty value.
 */
export function useCvsReviewedCount(): number {
  const [value, setValue] = useState(loaded ?? CVS_REVIEWED_COUNT);

  useEffect(() => {
    let active = true;
    void loadCvsReviewedCount().then((count) => {
      if (active && count !== null) {
        setValue(count);
      }
    });
    return () => {
      active = false;
    };
  }, []);

  return value;
}

/** Test hook: forget the shared request between cases. */
export function resetCvsReviewedCountCache() {
  pending = null;
  loaded = null;
}
